import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = true }: NavbarProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // If transparent is false, always show scrolled appearance
  const showScrolled = scrolled || !transparent;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        {/* Left: Logo + Desktop Nav */}
        <div className="flex items-center gap-8">
          <a
            href="/"
            className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors ${
              showScrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            acbuyspreadsheet<span className="text-primary-500">.bond</span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-5">
            <a
              href="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                showScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-white/80 hover:text-white"
              }`}
            >
              Home
            </a>
            <a
              href="/shopping-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                showScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-white/80 hover:text-white"
              }`}
            >
              Shopping Guide
            </a>
            <a
              href="/what-is-acbuyspreadsheet"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                showScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-white/80 hover:text-white"
              }`}
            >
              Guide
            </a>
          </div>
        </div>

        {/* Right: Desktop actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Categories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                showScrolled
                  ? "border-foreground-300 text-foreground-700 hover:border-foreground-500 hover:text-foreground-950"
                  : "border-white/40 text-white hover:border-white/80"
              }`}
            >
              Categories
              <i className={dropdownOpen ? "ri-arrow-up-s-line transition-transform text-xs" : "ri-arrow-down-s-line transition-transform text-xs"}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-[500px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap border-b border-background-100 last:border-0"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.imgSrc} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-foreground-900">{cat.name}</span>
                      <span className="text-xs text-foreground-500 truncate">{cat.description}</span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              showScrolled
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-white text-foreground-950 hover:bg-white/90"
            }`}
          >
            Buy It Now
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
            showScrolled ? "text-foreground-950 hover:bg-background-200" : "text-white hover:bg-white/10"
          }`}
        >
          <i className={mobileMenuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-background-50 border-t border-background-200 shadow-xl max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a href="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-home-line text-primary-600 text-sm"></i>
              </span>
              Home
            </a>
            <a href="/shopping-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-book-open-line text-accent-600 text-sm"></i>
              </span>
              Shopping Guide
            </a>
            <a href="/what-is-acbuyspreadsheet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-guide-line text-secondary-600 text-sm"></i>
              </span>
              Guide
            </a>

            {/* Mobile categories toggle */}
            <button
              onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-100">
                  <i className="ri-grid-line text-foreground-600 text-sm"></i>
                </span>
                Categories
              </span>
              <i className={`text-sm transition-transform ${mobileCategoryOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
            </button>

            {mobileCategoryOpen && (
              <div className="ml-11 space-y-1 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.imgSrc} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-2"></div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 mx-3 mt-2 px-5 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-line text-sm"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}